import { ExternalLink, Star } from 'lucide-react';

import type { DashboardNotificationItem, DashboardNotificationSeverity } from '../../types';
import { cn } from '../../utils/cn';
import { Button, Modal } from '../ui';
import {
    NOTIFICATION_SEVERITIES,
    formatNotificationTime,
    severityDots,
    severityLabels,
    sourceLabels,
} from './notificationMeta';

interface NotificationDetailModalProps {
    item: DashboardNotificationItem | null;
    // Admins and managers; others see the priority but can't change it.
    canChangePriority: boolean;
    onClose: () => void;
    onGoTo: (item: DashboardNotificationItem) => void;
    onSetPriority: (item: DashboardNotificationItem, severity: DashboardNotificationSeverity) => void;
    onToggleStar: (item: DashboardNotificationItem) => void;
    onMarkUnread: (item: DashboardNotificationItem) => void;
    // Where opening doesn't already count as seeing it, an unread one gets an "Đánh dấu đã xem" button.
    onMarkRead?: (item: DashboardNotificationItem) => void;
}

export function NotificationDetailModal({
    item,
    canChangePriority,
    onClose,
    onGoTo,
    onSetPriority,
    onToggleStar,
    onMarkUnread,
    onMarkRead,
}: NotificationDetailModalProps) {
    if (!item) return null;
    const defaultSeverity = item.source === 'MANUAL' ? null : (item.defaultSeverity ?? item.severity);
    const editable = canChangePriority && !item.expired;

    return (
        <Modal
            open
            title={item.title}
            description={[sourceLabels[item.source], item.campusName, formatNotificationTime(item.createdAt, true)].filter(Boolean).join(' · ')}
            onClose={onClose}
            footer={(
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-wrap gap-2">
                        <Button
                            variant="ghost"
                            size="sm"
                            icon={<Star className={cn('h-4 w-4', item.starred && 'fill-current text-warning-500')} />}
                            onClick={() => onToggleStar(item)}
                        >
                            {item.starred ? 'Bỏ sao' : 'Gắn sao'}
                        </Button>
                        {onMarkRead && !item.read ? (
                            <Button variant="secondary" size="sm" onClick={() => onMarkRead(item)}>
                                Đánh dấu đã xem
                            </Button>
                        ) : (
                            <Button variant="ghost" size="sm" onClick={() => { onMarkUnread(item); onClose(); }}>
                                Đánh dấu chưa xem
                            </Button>
                        )}
                    </div>
                    <Button size="sm" icon={<ExternalLink className="h-4 w-4" />} onClick={() => onGoTo(item)}>
                        Mở trang {sourceLabels[item.source]}
                    </Button>
                </div>
            )}
        >
            <div className="space-y-4 text-sm">
                {item.description ? <p className="whitespace-pre-line text-ink-700">{item.description}</p> : null}
                <fieldset>
                    <legend className="text-sm font-medium text-ink-900">Mức độ</legend>
                    <div className="mt-2 grid grid-cols-3 gap-2" role="radiogroup" aria-label="Mức độ">
                        {NOTIFICATION_SEVERITIES.map(severity => {
                            const selected = item.severity === severity;
                            return (
                                <button
                                    key={severity}
                                    type="button"
                                    role="radio"
                                    aria-checked={selected}
                                    disabled={!editable}
                                    onClick={() => !selected && onSetPriority(item, severity)}
                                    className={cn(
                                        'flex flex-col items-center gap-0.5 rounded-lg border px-2 py-2 text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-academy-200 disabled:cursor-not-allowed',
                                        selected ? 'border-academy-500 bg-academy-50 font-semibold text-academy-800' : 'border-ink-200 text-ink-700 enabled:hover:bg-ink-50',
                                        !editable && !selected && 'opacity-50',
                                    )}
                                >
                                    <span className="inline-flex items-center gap-1.5">
                                        <span className={cn('h-2 w-2 rounded-full', severityDots[severity])} />
                                        {severityLabels[severity]}
                                    </span>
                                    {defaultSeverity === severity ? <span className="text-[11px] font-normal text-ink-500">mặc định</span> : null}
                                </button>
                            );
                        })}
                    </div>
                </fieldset>
            </div>
        </Modal>
    );
}
